import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageLayout from '@/components/Layout/PageLayout';
import CardHeader from '@/components/Card/CardHeader';
import { useGetManagedAccount } from '@/api/hooks/managed-accounts/useGetManagedAccount';
import PaymentsManagerConfigurationForm from './PaymentsManagerConfigurationForm';
import PositivePayConfigurationForm from './PositivePayConfigurationForm';
import {
  MANAGED_ACCOUNT_TYPE,
  MANAGED_ACCOUNT_TYPE_TRANSLATION_KEY,
} from './managedAccount.constants';

const PageHeader = ({ name, onBack, backLabel }) => (
  <CardHeader color='primary'>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        component='button'
        type='button'
        aria-label={backLabel}
        onClick={onBack}
        sx={{
          display: 'flex',
          alignItems: 'center',
          background: 'none',
          border: 'none',
          p: 0,
          cursor: 'pointer',
          color: 'white',
        }}>
        <ArrowBack sx={{ color: 'white' }} />
      </Box>
      <AccountBalanceOutlined sx={{ color: 'white', mr: 1 }} />
      <Typography
        variant='h6'
        component='h1'
        sx={{ color: 'white', fontWeight: 500 }}>
        {name}
      </Typography>
    </Box>
  </CardHeader>
);

const ConfigurationSection = ({ managedAccount, onSaved }) => {
  const { t } = useTranslation();
  const translationKey =
    MANAGED_ACCOUNT_TYPE_TRANSLATION_KEY[managedAccount.type];
  const typeLabel = translationKey ? t(translationKey) : managedAccount.type;

  const renderForm = () => {
    if (managedAccount.type === MANAGED_ACCOUNT_TYPE.POSITIVE_PAY) {
      return (
        <PositivePayConfigurationForm
          managedAccountId={managedAccount.id}
          configuration={managedAccount.positivePayConfiguration}
          onSaved={onSaved}
        />
      );
    }

    if (managedAccount.type === MANAGED_ACCOUNT_TYPE.PAYMENTS_MANAGER) {
      return (
        <PaymentsManagerConfigurationForm
          managedAccountId={managedAccount.id}
          configuration={managedAccount.paymentsManagerConfiguration}
          onSaved={onSaved}
        />
      );
    }

    return (
      <Alert severity='warning'>
        {t('managedAccounts.detail.unsupportedType', {
          type: managedAccount.type,
        })}
      </Alert>
    );
  };

  return (
    <Accordion expanded disableGutters variant='outlined'>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls='managed-account-configuration-content'
        id='managed-account-configuration-header'>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant='h6'>{typeLabel}</Typography>
          {!managedAccount.paymentsManagerConfiguration &&
            !managedAccount.positivePayConfiguration && (
              <Chip
                size='small'
                color='warning'
                variant='outlined'
                label={t('managedAccounts.detail.notConfigured')}
              />
            )}
        </Box>
      </AccordionSummary>
      <AccordionDetails
        id='managed-account-configuration-content'
        sx={{ py: 3 }}>
        {renderForm()}
      </AccordionDetails>
    </Accordion>
  );
};

const ManagedAccount = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { managedAccount, loading, error, refetch } = useGetManagedAccount(id);

  return (
    <PageLayout
      height='auto'
      overflow='auto'
      header={
        <PageHeader
          name={managedAccount?.name ?? t('managedAccounts.detail.title')}
          backLabel={t('managedAccounts.detail.back')}
          onBack={() => navigate('/admin/managed-account')}
        />
      }>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label='Loading managed account' />
        </Box>
      )}

      {!loading && error && <Alert severity='error'>{error}</Alert>}

      {!loading && !error && managedAccount && (
        <ConfigurationSection
          managedAccount={managedAccount}
          onSaved={refetch}
        />
      )}
    </PageLayout>
  );
};

export default ManagedAccount;
